import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';
import { getLinkAttributes, getLinkText } from '../../../WYSIWYG/utils/links';
import { ConfigureLinkModal } from '../../components/ConfigureLinkModal';
import {
  ToggleButton,
  ToolbarIcon,
  TextEditorToolbarSection,
} from '../../styles';
import { useModalControl } from 'hooks/useModalControl';
import { useGridLikeFlexChildProps } from 'components/StandaloneToolBar/components/GridLikeFlex';
import { useMarkdownEditor } from 'components/WYSIWYG/MarkdownEditor/context';

export const LinkToolbarSection = () => {
  const { t } = useTranslation();
  const { editor, subscribeToSelectionUpdate, subscribeToUpdate } =
    useMarkdownEditor();
  const [ref, props] = useGridLikeFlexChildProps();
  const hasFocusedRef = useRef(false);
  const [modalOpen, { showModal, hideModal }] = useModalControl();
  const [defaultLinkProps, setDefaultLinkProps] = useState({});

  const [{ linkActive, canSetLink, canUnsetLink }, setState] = useState(() => ({
    linkActive: false,
    canSetLink: false,
    canUnsetLink: false,
  }));

  useEffect(() => {
    const handler = ({ editor }) => {
      if (!editor) {
        return;
      }
      const isLinkActive = editor.isActive('link');
      const isCodeBlockActive = editor.isActive('codeBlock');
      setState({
        linkActive: isLinkActive,
        canSetLink: !isCodeBlockActive && editor.can().setLink({}),
        canUnsetLink: isLinkActive,
      });
    };

    const selectionUnsubscribe = subscribeToSelectionUpdate(
      'link-toolbar-section',
      handler
    );
    const updateUnsubscribe = subscribeToUpdate(
      'link-toolbar-section',
      handler
    );

    return () => {
      selectionUnsubscribe();
      updateUnsubscribe();
    };
  }, [subscribeToSelectionUpdate, subscribeToUpdate]);

  useEffect(() => {
    if (editor) {
      const onFocus = () => {
        hasFocusedRef.current = true;
      };

      editor.on('focus', onFocus);

      return () => {
        editor.off('focus', onFocus);
      };
    }
  }, [editor]);

  if (!editor) return null;

  const handleModalClose = () => {
    hideModal();
    editor.chain().focus().run();
  };

  const handleLink = ({ href, linkText }) => {
    hideModal();
    setDefaultLinkProps({});
    const currentLink = getLinkAttributes(editor)?.href;

    editor
      .chain()
      .focus()
      .command(({ commands: { extendMarkRange } }) => {
        return currentLink
          ? extendMarkRange('link', { href: currentLink })
          : true;
      })
      .command(({ tr, editor }) => {
        const linkMark = editor.schema.mark('link', {
          href,
          target: '_blank',
        });
        const textNode = editor.schema.text(linkText, [linkMark]);
        tr.selection.replaceWith(tr, textNode);
        return true;
      })
      .run();
  };

  const handleUnlink = () => {
    editor.chain().focus().unsetLink().run();
  };

  const handleLinkClick = () => {
    const attrs = hasFocusedRef.current
      ? getLinkAttributes(editor)
      : getLinkAttributes(editor, { position: 'end' });
    const defaultLinkText = getLinkText(editor);
    const { href: defaultLink } = attrs;

    setDefaultLinkProps({
      defaultLinkText,
      defaultLink,
      alwaysOpenInNewTab: true,
    });
    showModal();
  };

  return (
    <TextEditorToolbarSection ref={ref} {...props}>
      {modalOpen && (
        <ConfigureLinkModal
          show={modalOpen}
          onConfirm={handleLink}
          onHide={handleModalClose}
          {...defaultLinkProps}
        />
      )}
      <ToggleButton
        active={linkActive}
        title={t('Link Text')}
        onClick={handleLinkClick}
        disabled={!canSetLink}
      >
        <ToolbarIcon icon="link" color="var(--color-background-darker)" />
      </ToggleButton>
      <ToggleButton
        title={t('Unlink Text')}
        onClick={handleUnlink}
        disabled={!canUnsetLink}
      >
        <ToolbarIcon icon="unlink" color="var(--color-background-darker)" />
      </ToggleButton>
    </TextEditorToolbarSection>
  );
};
